import { Injectable, EventEmitter, Output } from '@angular/core';
import { UserService } from './user.service';
import { HttpClient } from '@angular/common/http';
import { safeCb } from './user.service';
import { userRoles } from './user.service';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

// @flow
export class User {
    id: string = '';
    username: string = '';
    email: string = '';
    role: string = '';
    name?: string = '';
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    _currentUser: User = new User();
    @Output() currentUserChanged = new EventEmitter(true);
    userRoles = userRoles || [];
    UserService;

    // Authentication state observable
    private _authState = new BehaviorSubject<boolean>(false);
    authState$: Observable<boolean> = this._authState.asObservable();

    static parameters = [HttpClient, UserService];
    constructor(
        private http: HttpClient,
        private userService: UserService,
    ) {
        this.http = http;
        this.UserService = userService;

        // Check initial authentication state
        this.checkInitialAuthState();
    }

    // Check initial authentication state
    private checkInitialAuthState() {
        const token = localStorage.getItem('id_token');
        const user = localStorage.getItem('user');

        console.log('Initial Auth State Check:', { token, user });

        if (token && user) {
            try {
                const parsedUser = JSON.parse(user);
                console.log('Parsed User:', parsedUser);

                // Explicitly create and populate User instance
                const initialUser = new User();
                initialUser.id = parsedUser.id ? parsedUser.id.toString() : '';
                initialUser.username = parsedUser.username || '';
                initialUser.email = parsedUser.email || '';
                initialUser.role = parsedUser.role || '';
                initialUser.name = parsedUser.name || '';

                // Set current user and authentication state
                this._currentUser = initialUser;
                this.currentUserChanged.emit(initialUser);
                this._authState.next(true);

                console.log('Initial Authentication Setup:', {
                    userId: initialUser.id,
                    username: initialUser.username,
                    authState: true
                });
            } catch (error) {
                console.error('Error parsing user data', error);
                this._authState.next(false);
            }
        } else {
            // No valid token or user
            this._authState.next(false);
        }
    }

    private apiUrl = environment.backend;

    /**
     * Check if userRole is >= role
     * @param {String} userRole - role of current user
     * @param {String} role - role to check against
     */
    static hasRole(userRole, role) {
        return userRoles.indexOf(userRole) >= userRoles.indexOf(role);
    }

    get currentUser() {
        return this._currentUser;
    }

    set currentUser(user) {
        this._currentUser = user;
        this.currentUserChanged.emit(user);
    }

    /**
     * Authenticate user and save token
     *
     * @param  {Object}   user     - login info
     * @param  {Function} [callback] - function(error, user)
     * @return {Promise}
     */
    login({ email, password }, callback) {
        return this.http.post(`${this.apiUrl}/auth/local`, {
            email,
            password
        })
            .toPromise()
            .then((res: { token: string }) => {
                localStorage.setItem('id_token', res.token);
                return this.UserService.get().toPromise();
            })
            .then((user: User) => {
                // Explicitly create a new User instance with all properties
                const newUser = new User();
                newUser.id = user.id ? user.id.toString() : '';
                newUser.username = user.username || '';
                newUser.email = user.email || '';
                newUser.role = user.role || '';
                newUser.name = user.name || '';

                // Set current user and update local storage
                this._currentUser = newUser;
                localStorage.setItem('user', JSON.stringify(newUser));

                // Emit user changed event and update authentication state
                this.currentUserChanged.emit(newUser);
                this._authState.next(true);

                console.log('Login successful:', {
                    userId: newUser.id,
                    username: newUser.username,
                    authState: true
                });

                return this.http.get(`${this.apiUrl}/api/organisation/${user.id}/byiduser`).toPromise()
                    .then((organisationItem) => {
                        localStorage.setItem("organisation", JSON.stringify(organisationItem));
                        return newUser;
                    }).catch((error) => {
                        console.error('Error fetching organisation item', error);
                        return newUser;
                    });
            })
            .then((user) => {
                safeCb(callback)(null, user);
                return user;
            })
            .catch(err => {
                console.error('Login failed', err);
                this.logout();
                safeCb(callback)(err);
                return Promise.reject(err);
            });
    }

    /**
     * Delete access token and user info
     * @return {Promise}
     */
    logout() {
        localStorage.removeItem('user');
        localStorage.removeItem('organisation');
        localStorage.removeItem('id_token');
        this.currentUser = new User();
        this._authState.next(false); // Explicitly update auth state
        console.log('Logout successful, auth state set to false');
        return Promise.resolve();
    }

    /**
     * Create a new user
     *
     * @param  {Object}   user     - user info
     * @param  {Function} callback - optional, function(error, user)
     * @return {Promise}
     */
    createUser(user, callback) {
        return this.UserService.create(user).toPromise()
            .then(data => {
                localStorage.setItem('id_token', data.token);
                return this.UserService.get().toPromise();
            })
            .then((_user: User) => {
                this.currentUser = _user;
                return safeCb(callback)(null, _user);
            })
            .catch(err => {
                this.logout();
                safeCb(callback)(err);
                return Promise.reject(err);
            });
    }

    /**
     * Change password
     *
     * @param  {String}   oldPassword
     * @param  {String}   newPassword
     * @param  {Function} [callback] - function(error, user)
     * @return {Promise}
     */
    changePassword(oldPassword, newPassword, callback) {
        return this.UserService.changePassword({ id: this.currentUser.id }, oldPassword, newPassword)
            .toPromise()
            .then(() => safeCb(callback)(null))
            .catch(err => safeCb(callback)(err));

        /*
    return this.UserService.changePassword({id: this.currentUser._id}, oldPassword, newPassword)
        .toPromise()
        .then(() => safeCb(callback)(null))
        .catch(err => safeCb(callback)(err));
        //*/
    }

    /**
     * Gets all available info on a user
     *
     * @param  {Function} [callback] - function(user)
     * @return {Promise}
     */
    getCurrentUser(callback?) {
        safeCb(callback)(this.currentUser);
        return Promise.resolve(this.currentUser);
    }

    /**
     * Gets all available info on a user
     *
     * @return {Object}
     */
    getCurrentUserSync() {
        return this.currentUser;
    }

    /**
     * Checks if user is logged in
     * @param {function} [callback]
     * @returns {Promise}
     */
    isLoggedIn(callback?) {
        // Check multiple conditions for being logged in
        const token = localStorage.getItem('id_token');
        const user = localStorage.getItem('user');

        // Parse user to ensure we have a valid user object
        let parsedUser = null;
        try {
            parsedUser = user ? JSON.parse(user) : null;
        } catch (error) {
            console.error('Error parsing user', error);
        }

        // More robust user validation
        const hasValidUser = parsedUser &&
            (parsedUser.id || parsedUser._id) &&
            parsedUser.username;


        // Determine logged in state based on multiple factors
        const isLoggedIn = !!(token && hasValidUser);

        // If logged in, ensure current user is set
        if (isLoggedIn && parsedUser) {
            const initialUser = new User();
            initialUser.id = parsedUser.id ? parsedUser.id.toString() : '';
            initialUser.username = parsedUser.username || '';
            initialUser.email = parsedUser.email || '';
            initialUser.role = parsedUser.role || '';
            initialUser.name = parsedUser.name || '';

            this._currentUser = initialUser;
            this.currentUserChanged.emit(initialUser);
            this._authState.next(true);
        }

        safeCb(callback)(isLoggedIn);
        return Promise.resolve(isLoggedIn);
    }

    /**
     * Checks if user is logged in
     * @returns {Boolean}
     */
    isLoggedInSync() {
        const token = localStorage.getItem('id_token');
        const user = localStorage.getItem('user');

        // Parse user to ensure we have a valid user object
        let parsedUser = null;
        try {
            parsedUser = user ? JSON.parse(user) : null;
        } catch (error) {
            console.error('Error parsing user', error);
        }

        // More robust user validation
        const hasValidUser = parsedUser &&
            (parsedUser.id || parsedUser._id) &&
            parsedUser.username;

        const isLoggedIn = !!(token && hasValidUser);


        // If logged in, ensure current user is set
        if (isLoggedIn && parsedUser) {
            const initialUser = new User();
            initialUser.id = parsedUser.id ? parsedUser.id.toString() : '';
            initialUser.username = parsedUser.username || '';
            initialUser.email = parsedUser.email || '';
            initialUser.role = parsedUser.role || '';
            initialUser.name = parsedUser.name || '';

            this._currentUser = initialUser;
        }

        return isLoggedIn;
    }

    /**
     * Check if a user is an admin
     *
     * @param  {Function|*} [callback] - optional, function(is)
     * @return {Promise}
     */
    isAdmin(callback?) {
        return this.getCurrentUser().then(user => {
            var is = user.role === 'admin';
            safeCb(callback)(is);
            return is;
        });
    }

    isAdminSync() {
        return this._currentUser.role === 'admin';
    }

    getRole(callback?) {
        return this.getCurrentUser().then(user => {
            var is = user.role;
            safeCb(callback)(is);
            return is;
        });
    }

    getRoleAsync() {
        return this._currentUser.role;
    }

    /**
     * Get auth token
     *
     * @return {String} - a token string used for authenticating
     */
    getToken() {
        return localStorage.getItem('id_token');
    }
}
